const CURRENCY_CODE_PATTERN = /\s+[A-Z]{3}\b/g;
const DEBUG = new URLSearchParams(window.location.search).has('ja_subs_debug');
const PRICE_SELECTOR = [
  '.shopify_subscriptions_app_block .in_widget_price',
  'product-price .price',
  'product-price .compare-at-price',
].join(',');
const UNIT_PRICE_SELECTOR = 'product-price .unit-price';
const ONE_TIME_PURCHASE_SELECTOR = [
  '.shopify_subscriptions_app_block .one_time_purchase_option_app_block input[type="radio"]',
  '.shopify_subscriptions_app_block input[data-radio-type="one_time_purchase"]',
].join(',');
const SUBSCRIPTION_BLOCK_SELECTOR = '.shopify_subscriptions_app_block';
const HAS_SELLING_PLAN_PARAM = new URLSearchParams(window.location.search).has('selling_plan');

let initialPurchaseOptionHandled = false;
let customerInteractedWithPurchaseOptions = false;

function debugLog(...args) {
  if (!DEBUG) return;

  console.log('[JA subscriptions]', ...args);
}

function stripCurrencyCode(value) {
  return value.replace(CURRENCY_CODE_PATTERN, '').trim();
}

function parseMoney(value) {
  if (!value) return null;

  const normalizedValue = stripCurrencyCode(value).replace(/,/g, '').trim();
  const amount = Number(normalizedValue.replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(amount)) return null;

  const symbolMatch = normalizedValue.match(/^[^\d-]+/);

  return {
    amount,
    symbol: symbolMatch ? symbolMatch[0].trim() : '',
  };
}

function getUnitPriceSuffix(element) {
  return stripCurrencyCode(getUnitPriceText(element))
    .replace(/^[^\d-]*[\d,.]+/, '')
    .trim();
}

function getUnitPriceText(element) {
  const visibleText = Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent)
    .join(' ')
    .trim();

  return visibleText;
}

function getSelectedPurchaseOptionPrice() {
  const checkedInput = document.querySelector(
    `${SUBSCRIPTION_BLOCK_SELECTOR} input[type="radio"]:checked`
  );
  if (!checkedInput) return null;

  const subscriptionGroup = checkedInput.closest('.subscription_group');
  if (checkedInput.dataset.radioType === 'selling_plan_group') {
    const selectedOption = subscriptionGroup?.querySelector('.group_dropdown option:checked');
    if (selectedOption?.dataset.variantPrice) {
      return selectedOption.dataset.variantPrice;
    }
  }

  return checkedInput.dataset.variantPrice || null;
}

function updateUnitPrices() {
  const selectedPrice = parseMoney(getSelectedPurchaseOptionPrice());
  if (!selectedPrice) return;

  document.querySelectorAll(UNIT_PRICE_SELECTOR).forEach((unitPriceElement) => {
    const quantityValue = Number(unitPriceElement.dataset.unitPriceQuantityValue);
    const referenceValue = Number(unitPriceElement.dataset.unitPriceReferenceValue || 1);
    if (!Number.isFinite(quantityValue) || quantityValue <= 0) return;

    const unitAmount = (selectedPrice.amount * referenceValue) / quantityValue;
    const suffix = getUnitPriceSuffix(unitPriceElement);
    const formattedUnitPrice = `${selectedPrice.symbol}${unitAmount.toFixed(2)}${
      suffix ? ` ${suffix}` : ''
    }`;

    if (getUnitPriceText(unitPriceElement) === formattedUnitPrice) return;

    Array.from(unitPriceElement.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.remove();
      }
    });

    unitPriceElement.append(document.createTextNode(` ${formattedUnitPrice}`));

    debugLog('Updated unit price', {
      selectedPrice,
      quantityValue,
      referenceValue,
      formattedUnitPrice,
    });
  });
}

function normalizePriceText(element) {
  const currentText = element.textContent;
  if (!currentText || !CURRENCY_CODE_PATTERN.test(currentText)) return;

  const normalizedText = stripCurrencyCode(currentText);
  element.textContent = normalizedText;
  debugLog('Removed currency code', {
    element,
    from: currentText.trim(),
    to: normalizedText,
  });
}

function normalizePrices(root = document) {
  if (!root?.querySelectorAll) return;

  const priceElements = root.querySelectorAll(PRICE_SELECTOR);
  debugLog('Scanning price elements', {
    count: priceElements.length,
    root,
  });

  priceElements.forEach(normalizePriceText);
}

function selectOneTimePurchase() {
  if (
    initialPurchaseOptionHandled ||
    customerInteractedWithPurchaseOptions ||
    HAS_SELLING_PLAN_PARAM
  ) {
    return true;
  }

  const oneTimePurchaseInput = document.querySelector(ONE_TIME_PURCHASE_SELECTOR);
  if (!oneTimePurchaseInput) return false;

  initialPurchaseOptionHandled = true;

  if (oneTimePurchaseInput.checked) {
    debugLog('One-time purchase already selected');
    return true;
  }

  debugLog('Selecting one-time purchase by default', {
    input: oneTimePurchaseInput,
  });

  oneTimePurchaseInput.click();

  if (!oneTimePurchaseInput.checked) {
    oneTimePurchaseInput.checked = true;
    oneTimePurchaseInput.dispatchEvent(new Event('input', { bubbles: true }));
    oneTimePurchaseInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  normalizePrices();
  updateUnitPrices();
  return true;
}

function normalizeMutationTarget(target) {
  const element = target instanceof Element ? target : target.parentElement;
  if (!element) return;

  if (element.matches(PRICE_SELECTOR)) {
    normalizePriceText(element);
    return;
  }

  const priceElement = element.closest?.(PRICE_SELECTOR);
  if (priceElement) {
    normalizePriceText(priceElement);
    return;
  }

  normalizePrices(element);
}

function observePriceChanges() {
  debugLog('Starting price observer');

  const observer = new MutationObserver((mutations) => {
    debugLog('Observed mutations', {
      count: mutations.length,
    });

    for (const mutation of mutations) {
      normalizeMutationTarget(mutation.target);

      mutation.addedNodes.forEach((node) => {
        normalizeMutationTarget(node);
      });
    }

    selectOneTimePurchase();
    updateUnitPrices();
  });

  observer.observe(document.documentElement, {
    childList: true,
    characterData: true,
    subtree: true,
  });
}

function observeCustomerPurchaseOptionChanges() {
  document.addEventListener(
    'change',
    (event) => {
      if (!event.isTrusted) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest(SUBSCRIPTION_BLOCK_SELECTOR)) return;

      customerInteractedWithPurchaseOptions = true;
      debugLog('Customer changed purchase option');
      window.requestAnimationFrame(updateUnitPrices);
    },
    true
  );
}

function observeSubscriptionHeaderClicks() {
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('input, select, button, a')) return;

      const subscriptionHeader = target.closest(
        '.shopify_subscriptions_app_block .subscription_group > .title_and_price_wrapper'
      );
      if (!subscriptionHeader) return;

      const subscriptionInput = subscriptionHeader.querySelector(
        'input[data-radio-type="selling_plan_group"]'
      );
      if (!subscriptionInput || subscriptionInput.checked) return;

      debugLog('Selecting subscription from header click', {
        input: subscriptionInput,
      });

      subscriptionInput.click();
      normalizePrices();
      updateUnitPrices();
    },
    true
  );
}

function init() {
  debugLog('Initializing subscription cleanup');
  observeCustomerPurchaseOptionChanges();
  observeSubscriptionHeaderClicks();
  selectOneTimePurchase();
  normalizePrices();
  updateUnitPrices();
  observePriceChanges();

  window.requestAnimationFrame(selectOneTimePurchase);
  window.requestAnimationFrame(() => {
    normalizePrices();
    updateUnitPrices();
  });
  window.setTimeout(selectOneTimePurchase, 100);
  window.setTimeout(() => {
    normalizePrices();
    updateUnitPrices();
  }, 250);
  window.setTimeout(selectOneTimePurchase, 500);
  window.setTimeout(() => {
    normalizePrices();
    updateUnitPrices();
  }, 750);
}

debugLog('Script loaded');

if (document.documentElement) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
